/**
 * @file CreatePurchaseOrder.js
 * @version 2.25.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-18
 * @modified 2026-11-03
 *
 * 2.22.0 (2026-10-29, Cam — "the PO-H generated from a third party hauled
 * PO-D (with no SO-D) should prompt user to send to haulage supplier
 * post-PO-D-save"): a save that raised a NEW PO-H for this stock or
 * self-billed PO (the answer's `haulage_raised` — never sent) asks "PO-H…
 * raised to <haulier> — send it now?" (Send opens the Send to Supplier window
 * for the PO-H; Not now). On a new PO it comes first, then the PO's own
 * "Purchase Order Created" window, which says whether the PO-H went; on an
 * edit it follows the revised / cancellation offers (a new haulier's PO-H
 * after the old one's cancellation), before the saved message.
 *
 * 2.21.0 (2026-10-29, Cam — "when we create a PO-D (not from an SO-D) and
 * choose Third Party Collection for supplier terms, we should get the same
 * offer of hauliers as in the same situation when creating an SO-D, and a PO-H
 * should be generated and linked"): a STOCK PO (no customer order, no line
 * for an order line, customer "None — delivered to us", not self-billed —
 * utils/stockHaulage) on Third-Party Collection asks for its Third-party
 * haulier (required) and Delivery service (Standard unless chosen) under
 * Deliver To, as a self-billed PO on third-party terms does; Our Delivery
 * Terms reads Third-Party Delivery (the pair); packed lines need pallets
 * (the same rule and words, utils/lineUnits); the server raises and keeps its
 * PO-H to our site. A PO naming a customer on Third-Party Collection says why
 * it gets none.
 *
 * 2.20.0 (2026-10-22, Cam — "Xero-made POs should be editable too and synced
 * with Xero at time of save"): saving a PO made in Xero sends its lines to
 * Xero; if Xero's copy changed since it was read (409 XERO_PO_NEWER) the
 * editor asks "Send ours to Xero" and saves again with xero_newer_confirmed.
 *
 * 2.11.0 (2026-10-09, Cam): a delivered (or invoiced) order's PO opens here
 * for corrections — line words, item codes (written back to the order line),
 * contract numbers, references, instructions, the supplier and its terms. The
 * quantity, cost, tax and additive cells, adding and removing lines, the
 * delivery date, status and customer are greyed with the reason (the PO's
 * `afterDelivery`; utils/afterDelivery). A reason is optional (a field in
 * the note, kept in the history when given); nothing is offered to the
 * supplier as a revised PO. An item code that reaches an
 * invoiced order offers the invoice the change (InvoiceEditOfferDialog).
 *
 * 2.18.0 (2026-10-15, Cam): an ARCHIVED PO given a supplier reference is
 * saved with the reference and stays Archived by default (it is there for
 * reference) — "Set Confirmed…" is the other choice, never the default. Any
 * save that takes an archived PO off Archived (that choice, the Confirmed
 * question, or the status picked by hand) asks "are you sure?" once: it
 * becomes a live PO again without its order lines (Restore re-links them).
 *
 * 2.17.0 (2026-10-15, Cam — PO-D10530): a line bought for an order line
 * cannot be removed here (its delete button greyed with the reason; the
 * server refuses it, 409 ORDER_LINE_REMOVED). To change the product, choose
 * the new item on that line — it is written back to the order line.
 *
 * 2.16.0 (2026-10-14, Cam — PO-D10279): on a delivered (or invoiced)
 * order's PO the Status opens again, FORWARD only (utils/poStatusMove):
 * the status it was loaded with and the steps after it are offered, the rest
 * greyed with the reason. Marking it Sent or Confirmed emails nobody. The
 * "mark as confirmed?" question after a supplier reference is added is asked
 * after delivery too, whenever Confirmed is still a forward move.
 *
 * 2.13.0 (2026-10-12, Cam): SELF-BILLED (off by default) — beside "Show
 * quantities as", in the column Status takes in the row above. On, the PO is
 * us buying from a customer (utils/selfBilledPo): its customer is fixed to
 * "None — delivered to us" (greyed with the reason), "Deliver To" becomes a
 * required "Delivery / storage address" — every contact, with STOCK (our own
 * site) pinned and sticky first — and both sides' delivery terms take any
 * value (Our Delivery Terms opens for it). The server works the address out
 * from the contact (services/selfBilledPoService). The switch is greyed with
 * the reason on a PO for a customer order, a billed or archived PO, and
 * before the database update.
 *
 * 2.13.1 (2026-10-13, review): each line is saved with the haulier, driver
 * and vehicle it was LOADED with (`_loadedTransport`), so the server keeps
 * whatever a planning board set meanwhile on a line the user did not change
 * here; the editor says the board may set them too.
 *
 * 2.15.0 (2026-10-13, Cam): a self-billed PO on third-party terms chooses the
 * delivery service its PO-H is booked for ("Delivery service", Standard by
 * default) beside the haulier; before the database holds Standard the editor
 * says it will be saved with none.
 *
 * 2.14.0 (2026-10-13, Cam): the one transport rule (utils/transportLock):
 * on a planning board (an order the boards show, or a self-billed PO we
 * carry) the Transport cell is read-only up to and including the delivery
 * date — the board's — and from the day after a correction for holders of
 * Edit Transport Planning (the board's record of that day is corrected with
 * it). Third-Party and Direct Delivery PO-Ds, and POs with no order, are set
 * here as before. A self-billed PO on Third-Party terms names its haulier
 * (required) and shows its PO-H.
 *
 * Three sections, and only what someone raising a PO decides:
 *
 *   PO Details        supplier (with + New Supplier), created and delivery
 *                     dates, the PO number, supplier ref, status
 *   Line Details      the lines grid
 *   Delivery Details  where it is delivered, and the instructions
 *
 * Everything else a PO carries — supplier address, phone and email, our
 * reference, delivery address — is still loaded, filled and saved, just not
 * shown: a supplier's details follow the supplier picked, a stock PO delivers
 * to us, and a PO raised from a customer order keeps what that order gave it.
 *
 * The customer IS shown, under Delivery Details, and can be changed on an
 * edit: the delivery address follows it, and on a PO raised for a customer
 * order the server moves the ORDER to the new customer — and its other POs
 * and PO-H with it — unless the order is invoiced (routes/purchaseOrders.js,
 * services/orderCustomerService.js). A PO a supplier already holds is then
 * offered for re-sending as a revised order.
 *
 * Lines are the shared editable grid (OrderLinesGrid, variant "purchase"), the
 * same one Add Customer Order, Add Invoice and Add Bill use, with haulier,
 * driver and vehicle stacked in one Transport cell.
 *
 * The add-line form and read-only table it replaced stored each line amount
 * with tax already in it, and the server adds tax on top when it totals a PO —
 * so every PO typed in here was over-totalled by its VAT. Additives were chosen
 * but never sent, and editing a Xero-synced PO stripped the line fields this
 * screen did not show (accountCode, tracking, lineItemID). Lines now map to and
 * from the PO's JSONB in utils/lineItems, net, with everything else carried
 * through untouched.
 *
 * 2.10.0 (2026-10-08, Cam): "Show quantities as" — Tonnes first / Packs
 * first, how a line counted in packs prints for the supplier
 * (utils/poQtyDisplay). Under the two delivery-terms fields, on its own row of
 * the details grid so the eight columns above keep their widths. A new PO
 * opens on the default (packs first when raised from a Third-Party order,
 * tonnes first otherwise); an edit opens on the PO's own. Presentation only;
 * hidden behind a disabled control with the reason until the database update
 * has run.
 *
 * 2.10.1 (2026-10-09): Edit opens as the editor. editPOId is read on the first
 * render (spinner from the start) and the PO is fetched alongside the supplier
 * and item lists — it used to be read only after both lists had loaded, so
 * Edit showed a blank "Add" form for seconds. A PO with no supplierId is
 * matched against the supplier list just loaded (the closure's was always
 * empty), and a PO that cannot be loaded says so instead of leaving the form
 * blank.
 *
 * 2.19.0 (2026-10-21, Cam — "The option to show packs or tonnes first is
 * confusing"): "Show quantities as" is gone. A line counted in packs shows its
 * packs first and a tonne line its tonnes, on the PDF and the PO window alike
 * (the server's utils/poQtyDisplay) — the unit chosen on the line decides. The
 * grid also costs a line per its own unit (OrderLinesGrid 2.19.0,
 * utils/itemCostUnit) and asks what an item's cost is per when the item has
 * no unit.
 *
 * 2.23.0 (2026-10-30, Cam — the Item picker's X): each "Recently bought" row
 * can be taken off this supplier's list for everyone until it is bought
 * again, with Undo (hooks/usePreviousItemRemoval).
 *
 * 2.24.0 (2026-11-02, Cam — the item's supplier): the Supplier list pins each
 * line's item's supplier at the top (a supplier group's depots, when the item
 * is a group's), in view while the rest scrolls; picking an item fills the
 * Supplier only when none is chosen yet and every line's item names the same
 * ONE supplier — never a supplier group (one of its depots is picked).
 * utils/itemSupplier.
 *
 * 2.25.0 (2026-11-03, Cam — "Typed lines should require user to input sales
 * and purchase account codes … (and invoices or whatever)"): a typed line (no
 * inventory item) shows a Purchase account under its item (the purchase
 * accounts list); a new or changed one with none is outlined and Save is
 * greyed with the reason (utils/lineAccountRequired — the server refuses it
 * too, 400 ACCOUNT_REQUIRED). A line bought for an order line takes its order
 * line's (set on the order), so it is not asked here.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import { formatAddress, formatAddressFromFlat } from '../utils/formatAddress';
import './DocumentForm.css';
import './CreatePurchaseOrder.css';
import Input from './Input';
import DateInput from './DateInput';
import SearchableDropdown from './SearchableDropdown';
import Dropdown from './Dropdown';
import AlertDialog from './AlertDialog';
import { useSlideOutButton } from './SlideOutButton';
import LoadingSpinner from './LoadingSpinner';
import InventoryItemFormModal from './InventoryItemFormModal';
import OrderLinesGrid, { blankLine, lineIsEmpty, lineTotals, LineTotalsPanel } from './OrderLinesGrid';
import { poLinesFromApi, poLinesToPayload, resolveItemForVariant } from '../utils/lineItems';
import { wordsOnItemPick } from '../utils/pickedItemWords';
import ContactEditModal from './ContactEditModal';
import ContactModal from './ContactModal';
import ConfirmDialog from './ConfirmDialog';
import SendToSupplierFlow from './SendToSupplierFlow';
import PostedEditDialog from './PostedEditDialog';
import InvoiceEditOfferDialog from './InvoiceEditOfferDialog';
import { poClosedCells, hasInvoiceOffer } from '../utils/afterDelivery';
import { PO_STATUS_OPTIONS, DEFAULT_PO_STATUS, normalisePoStatus, poStatusLabel, isArchivedPoStatus } from '../utils/poStatus';
import { statusMoveAfterDelivery } from '../utils/poStatusMove';
import {
  SUPPLIER_DELIVERY_TERMS_OPTIONS, ourDeliveryTermsOptions, pairOnChoose, isCustomerCollection,
  CUSTOMER_COLLECTION_LABEL, DIRECT_DELIVERY_TERMS, THIRD_PARTY_SUPPLIER_TERMS, THIRD_PARTY_OUR_TERMS,
  HAULAGE_SERVICE_OPTIONS, STANDARD_HAULAGE_SERVICE
} from '../utils/deliveryTerms';

import { withStoreTag } from '../utils/stockSupplier';
import { headerSupplierFill, pinnedSupplierIds, pinSupplierOptions } from '../utils/itemSupplier';
import { revealInSearch } from '../utils/revealInSearch';
import { poHasCustomerOrder } from '../utils/poTransportLock';
import {
  poTransportRule, transportChangeRefusal, londonToday, PATH_PO_EDITOR,
  TRANSPORT_PHASE_BOARD, TRANSPORT_PHASE_CORRECTION, TRANSPORT_PHASE_FREE
} from '../utils/transportLock';
import { hasPermission } from '../utils/permissions';
import usePreviousItemRemoval from '../hooks/usePreviousItemRemoval';
import { linesMissingPallets } from '../utils/lineUnits';
import { documentLineAccountRefusals, accountRefusalMessage } from '../utils/lineAccountRequired';
import {
  stockThirdParty, stockOurTerms, stockHaulageBlock, STOCK_HAULIER_REQUIRED_MESSAGE, STOCK_HAULAGE_HINT, STOCK_OUR_TERMS_HINT
} from '../utils/stockHaulage';
import {
  SELF_BILLED_HINT, ADDRESS_PLACEHOLDER, ADDRESS_REQUIRED_MESSAGE, CUSTOMER_FIXED_REASON, STOCK_BADGE, STOCK_OPTION_VALUE,
  DELIVERY_TO_STOCK, DELIVERY_TO_CONTACT, SELF_BILLED_OUR_TERMS, OUR_SITE_NAME,
  selfBilledToggleBlock, selfBilledTermsProblem, stockAddressText
} from '../utils/selfBilledPo';

/*
 * Direct Delivery and Third-Party change haulage (the supplier's haulage lines,
 * a PO-H), so on a PO raised from an order they are chosen on the ORDER only —
 * offered here just as the value already held (Cam, 2026-10-02).
 */
const ORDER_ONLY_TERMS = [DIRECT_DELIVERY_TERMS, THIRD_PARTY_SUPPLIER_TERMS, THIRD_PARTY_OUR_TERMS];
const withoutOrderOnly = (options, current) =>
  options.filter(o => !ORDER_ONLY_TERMS.includes(o.value) || o.value === current);

/*
 * On a self-billed PO Third-Party is a pair as everywhere (the server
 * refuses one side alone): choosing it on one side sets the other. Leaving it
 * changes nothing, like Customer Collection's pairOnChoose.
 */
const pairForSelfBilled = (side, value, next) => {
  if (side === 'supplier' && value === THIRD_PARTY_SUPPLIER_TERMS) return { ...next, ours: THIRD_PARTY_OUR_TERMS };
  if (side === 'ours' && value === THIRD_PARTY_OUR_TERMS) return { ...next, supplier: THIRD_PARTY_SUPPLIER_TERMS };
  return next;
};

// A self-billed PO's own terms (ours) — every value, Direct Delivery included.
const SELF_BILLED_OUR_TERMS_OPTIONS = SELF_BILLED_OUR_TERMS.map(t => ({ value: t, label: t }));

/** What the Status field says on a delivered (or invoiced) order's PO. */
export const AFTER_DELIVERY_STATUS_HINT = 'Its order is delivered — the status can only move forward (marking it sent emails nobody)';

/**
 * The Status options on a delivered order's PO (utils/poStatusMove): the
 * status it was loaded with and the steps after it; the rest greyed, each
 * with its reason. Disable, never hide.
 */
export function statusOptionsAfterDelivery(loaded) {
  const from = normalisePoStatus(loaded) || 'DRAFT';
  const options = PO_STATUS_OPTIONS.some(o => o.value === from)
    ? PO_STATUS_OPTIONS : [...PO_STATUS_OPTIONS, { value: from, label: poStatusLabel(from) }];
  return options.map((o) => {
    const why = statusMoveAfterDelivery(from, o.value);
    return why ? { ...o, disabled: true, title: why.charAt(0).toUpperCase() + why.slice(1) } : o;
  });
}

/** A contact's address, as the PO prints it: name first, then its lines. */
export function contactDeliveryText(contact, formatFlat = formatAddressFromFlat, formatObj = formatAddress) {
  if (!contact) return '';
  let lines = '';
  if (contact.delivery_line1) lines = formatFlat(contact, 'delivery_', { separator: '\n' });
  else if (contact.invoice_line1) lines = formatFlat(contact, 'invoice_', { separator: '\n' });
  else if (contact.address) {
    let a = contact.address;
    if (typeof a === 'string' && a.startsWith('{')) { try { a = JSON.parse(a); } catch (e) { /* plain text */ } }
    lines = formatObj(a, { separator: '\n' });
  }
  const name = String(contact.company_name || '').trim();
  const rest = String(lines || '').split('\n').map(l => l.trim()).filter(l => l && l.toLowerCase() !== name.toLowerCase());
  return [name, ...rest].filter(Boolean).join('\n');
}

/**
 * The Delivery / storage address picker's options: STOCK (our own site)
 * pinned and sticky first, then every contact by name.
 */
export function deliveryTargetOptions(contacts) {
  return [
    { value: STOCK_OPTION_VALUE, badge: STOCK_BADGE, label: `${OUR_SITE_NAME} — our own site`, sticky: true,
      searchText: 'stock our site excel agri' },
    ...(contacts || []).map(c => ({
      value: String(c.id),
      badge: c.account_no || ' ',
      label: c.company_name,
      searchText: `${c.account_no || ''} ${c.company_name} ${c.delivery_postcode || c.invoice_postcode || ''}`
    }))
  ];
}

function CreatePurchaseOrder({ user, onNavigate }) {
  // Read on the first render, so an edit opens as the editor (a spinner, then
  // the PO) rather than as a blank new PO while the lists load.
  const [initialEditId] = useState(() =>
    (typeof window !== 'undefined' ? sessionStorage.getItem('editPOId') : null));
  const [editMode, setEditMode] = useState(!!initialEditId);
  const [editingPOId, setEditingPOId] = useState(initialEditId);
  const [creatingFromOrderId, setCreatingFromOrderId] = useState(null);
  const [loading, setLoading] = useState(!!initialEditId);
  // Its order delivered or invoiced: what stays closed, and why (the server's afterDelivery).
  const [poAfterDelivery, setPoAfterDelivery] = useState(null);
  // Why it is being corrected after delivery — optional, kept in the history when given.
  const [correctionReason, setCorrectionReason] = useState('');
  // An item code that reached an invoiced order: the invoice's offer, then what comes next.
  const [invoiceOffer, setInvoiceOffer] = useState(null);

  // PO Header Fields
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [supplierAddress, setSupplierAddress] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [supplierEmail, setSupplierEmail] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  // The customer order this PO was raised for, if any — its customer follows.
  const [linkedOrderNumber, setLinkedOrderNumber] = useState(null);
  // The orders this PO buys for (terms and dates) and its own delivery date as
  // loaded — what the transport rule reads (utils/transportLock).
  const [linkedOrdersTransport, setLinkedOrdersTransport] = useState([]);
  const [loadedDeliveryDate, setLoadedDeliveryDate] = useState(null);
  const [poDate, setPODate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [rawDeliveryAddress, setRawDeliveryAddress] = useState(null); // Preserves country code for save
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  // A new PO starts as Collection — we fetch it — which is what most of them
  // are. An existing PO keeps what it was saved with (see loadPurchaseOrder).
  // No default — chosen on every PO (Cam, 2026-09-29). A PO loaded for edit
  // or pre-filled from an order brings its own.
  const [supplierDeliveryTerms, setSupplierDeliveryTerms] = useState('');
  /*
   * OUR delivery terms — the customer order's, shown beside the supplier's and
   * kept in step with them both ways (the server writes a change through to
   * the order). Only a PO raised for an order has any; '' otherwise.
   */
  const [ourDeliveryTerms, setOurDeliveryTerms] = useState('');
  const [reference, setReference] = useState('');
  const [supplierReference, setSupplierReference] = useState('');
  // The reference this PO was carrying when it was opened for editing.
  const loadedSupplierRefRef = useRef('');
  // The customer this PO had when it was opened for editing — sent back so a
  // save made after the order was diverted is refused, not moved back
  // (409 CUSTOMER_CHANGED_SINCE_LOADED).
  const loadedCustomerIdRef = useRef('');
  // General-purpose confirm, separate from confirmReset which is its own flag.
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  // A billed PO, with posted editing switched on: the server asks why before
  // it saves (EDIT_REASON_REQUIRED). Holds the save's options to retry with.
  const [reasonPrompt, setReasonPrompt] = useState(null);
  const [status, setStatus] = useState(DEFAULT_PO_STATUS);
  // The status as loaded: after delivery it may only move forward from it.
  const [loadedStatus, setLoadedStatus] = useState(null);
  /*
   * SELF-BILLED (utils/selfBilledPo). `deliveryTarget` is the Delivery /
   * storage address picked: STOCK_OPTION_VALUE, a contact's id, or '' (not
   * yet — required). `selfBilledServerBlock` is the server's reason the
   * switch cannot change on this PO (an edit). Every contact is loaded only
   * once the switch is on.
   */
  const [selfBilled, setSelfBilled] = useState(false);
  const [selfBilledReady, setSelfBilledReady] = useState(false);
  // Third-party haulage on a self-billed PO: who collects it (a supplier
  // contact), and the PO-H raised for them; asked for once the database can
  // hold it.
  const [selfBilledHaulageReady, setSelfBilledHaulageReady] = useState(false);
  const [selfBilledHaulierId, setSelfBilledHaulierId] = useState('');
  // The delivery service its PO-H is booked for — Standard unless chosen
  // (Cam, 13 Oct 2026). Standard is stored once the database can hold it.
  const [selfBilledHaulageService, setSelfBilledHaulageService] = useState(STANDARD_HAULAGE_SERVICE);
  const [haulageStandardReady, setHaulageStandardReady] = useState(true);
  const [selfBilledHaulagePo, setSelfBilledHaulagePo] = useState(null);
  const [selfBilledServerBlock, setSelfBilledServerBlock] = useState(null);
  const [deliveryTarget, setDeliveryTarget] = useState('');
  const [allContacts, setAllContacts] = useState(null);
  // The PO's own number when editing; on a new PO, the number it will most
  // likely be given — see GET /api/purchase-orders/next-number.
  const [poNumber, setPoNumber] = useState('');
  const [nextPoNumber, setNextPoNumber] = useState('');
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  // Captured before the edit effect clears the flag: only a brand-new PO opens
  // with the supplier search already waiting for typing.
  const [autoOpenSupplier] = useState(() =>
    typeof window !== 'undefined'
      && !sessionStorage.getItem('editPOId')
      && !sessionStorage.getItem('createPOFromOrderId'));
  const [hauliers, setHauliers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  // Line items, in the grid's shape. A fresh PO opens with one empty row.
  const [lineItems, setLineItems] = useState(() => [blankLine()]);

  // Data Lists
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  // The purchase accounts a typed line chooses from (2.25.0).
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  // Items bought from this supplier before, for the Item picker.
  const [previousItems, setPreviousItems] = useState([]);
  // The picker's X: take a row off this supplier's "Recently bought" for
  // everyone until it is bought again (Cam, 30 Oct 2026), with Undo.
  const { removePrevious, toast: previousItemToast } = usePreviousItemRemoval({
    side: 'supplier',
    accountId: selectedSupplierId,
    accountName: suppliers.find(s => String(s.id) === String(selectedSupplierId))?.company_name || '',
    previousItems,
    setPreviousItems,
  });
  // Creating an item from the Item picker: which row asked.
  const [addItemForRow, setAddItemForRow] = useState(null);

  // Dialogs
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info', onCloseCallback: null });
  const [postSaveDialog, setPostSaveDialog] = useState({ isOpen: false, poNumber: '', poId: null });
  // The PO just created, being sent to its supplier (SendToSupplierFlow).
  const [sendFlowPo, setSendFlowPo] = useState(null);
  // After an edit that changed POs a supplier already holds: the offer to send
  // the revised versions, then the flow sending them.
  const [revisedPrompt, setRevisedPrompt] = useState(null);
  const [revisedFlow, setRevisedFlow] = useState(null);
  // A PO-H this save newly raised for the haulier (never sent): the offer to
  // send it now, then the flow sending it (2026-10-29).
  const [haulagePrompt, setHaulagePrompt] = useState(null);
  const [haulageFlow, setHaulageFlow] = useState(null);
  const haulageSentRef = useRef(false);

  useSlideOutButton([postSaveDialog.isOpen, editMode, loading, selectedSupplierId]);

  useEffect(() => {
    const initializeComponent = async () => {
      // Load reference data first
      loadCustomers();
      loadHauliers();
      loadDrivers();
      loadVehicles();

      // Editing an existing PO: fetch it alongside the lists below.
      const editPOId = initialEditId;
      let poFetch = null;
      if (editPOId) {
        sessionStorage.removeItem('editPOId');
        poFetch = fetch(`${API_URL}/api/purchase-orders/${editPOId}`, { credentials: 'include' })
          .then(r => r.json());
        poFetch.catch(() => {}); // handled in loadPOForEdit
      }

      // Wait for suppliers (so the dropdown can show the PO's supplier) and the
      // item list (passed on explicitly: `suppliers` / `products` state is
      // still empty in this closure, and saved lines are matched back to
      // inventory by it).
      loadPurchaseAccounts();
      const [supplierList, items] = await Promise.all([loadSuppliers(), loadProducts()]);

      if (editPOId) {
        loadPOForEdit(editPOId, items, supplierList, poFetch);
        return;
      }

      loadNextPoNumber();

      // Check if we're creating PO from a customer order
      const createFromOrderId = sessionStorage.getItem('createPOFromOrderId');
      if (createFromOrderId) {
        sessionStorage.removeItem('createPOFromOrderId');
        setCreatingFromOrderId(createFromOrderId);
        loadPOFromOrder(createFromOrderId, items);
      }
    };

    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-populate delivery address when customer is selected (only for new POs, not edits)
  useEffect(() => {
    // Skip auto-population in edit mode - delivery address is loaded from the PO data
    if (editMode) {
      return;
    }

    if (selectedCustomerId) {
      const customer = customers.find(c => c.id.toString() === selectedCustomerId);
      const address = getCustomerDeliveryAddress(customer);
      if (address) {
        setDeliveryAddress(address);
      }
    } else {
      // Use Excel Agri default address if no customer selected
      setDeliveryAddress('Excel Agri Ltd.\nThe Old Grain Drier\nDolton Beacon\nWinkleigh\nDevon\nEX19 8PS');
    }
  }, [selectedCustomerId, customers, editMode]);

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCustomers(data.contacts || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=SUPPLIER&limit=10000`, {
        credentials: 'include'
      });
      const data = await response.json();
      const list = data.contacts || [];
      setSuppliers(list);
      return list;
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      return [];
    }
  };

  const loadPurchaseAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/purchase-accounts`, { credentials: 'include' });
      const data = await response.json();
      setPurchaseAccounts(data.accounts || []);
    } catch (error) {
      console.error('Failed to load purchase accounts:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(// activeOnly=false so OrderItemPicker has archived items to offer behind
      // its toggle. They stay hidden until asked for, so the list this opens
      // onto is unchanged.
      `${API_URL}/api/inventory/search?limit=10000&isPurchased=true&activeOnly=false`, {
        credentials: 'include'
      });
      const data = await response.json();
      const items = data.success ? (data.items || []) : [];
      setProducts(items);
      return items;
    } catch (error) {
      console.error('Failed to load products:', error);
      return [];
    }
  };

  // What has been bought from this supplier before, looked up by id.
  useEffect(() => {
    if (!selectedSupplierId) { setPreviousItems([]); return undefined; }
    let cancelled = false;
    fetch(`${API_URL}/api/purchase-orders/supplier-previous-items/${selectedSupplierId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (!cancelled) setPreviousItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPreviousItems([]); });
    return () => { cancelled = true; };
  }, [selectedSupplierId]);

  // Every contact (customers, suppliers, both), for a self-billed PO's
  // Delivery / storage address — asked for once the switch is on.
  useEffect(() => {
    if (!selfBilled || allContacts !== null) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&limit=10000`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (!cancelled) setAllContacts(data.contacts || []); })
      .catch(() => { if (!cancelled) setAllContacts([]); });
    return () => { cancelled = true; };
  }, [selfBilled, allContacts]);

  const loadNextPoNumber = async () => {
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/next-number`, { credentials: 'include' });
      const data = await res.json();
      setNextPoNumber(data.nextNumber || '');
      setSelfBilledReady(!!data.selfBilledReady);
      setSelfBilledHaulageReady(!!data.selfBilledHaulageReady);
      setHaulageStandardReady(data.haulageStandardReady !== false);
    } catch (error) {
      setNextPoNumber('');
    }
  };

  const loadHauliers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setHauliers(data.hauliers.map(h => h.name));
      }
    } catch (error) {
      console.error('Failed to load hauliers:', error);
      setHauliers([]);
    }
  };

  const loadDrivers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        // Keep full driver shape — name, hauliers (filter), default vehicle (auto-fill).
        setDrivers(data.drivers);
      }
    } catch (error) {
      console.error('Failed to load drivers:', error);
      setDrivers([]);
    }
  };

  const loadVehicles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transport/vehicles`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setVehicles(data.vehicles.map(v => v.registration));
      }
    } catch (error) {
      console.error('Failed to load vehicles:', error);
      setVehicles([]);
    }
  };

  const formatAddressForEdit = (address) => {
    return formatAddress(address, { separator: '\n', includeAttention: true });
  };

  // A contact's address arrives as an object, a JSON string or plain text.
  const contactAddressText = (address) => {
    let value = address || '';
    if (typeof value === 'string' && value.startsWith('{')) {
      try {
        value = JSON.parse(value);
      } catch (e) {
        // Not valid JSON, use as-is
      }
    }
    return formatAddressForEdit(value);
  };

  // Get the delivery address from a customer using flat columns
  const getCustomerDeliveryAddress = (customer) => {
    if (!customer) return '';

    // Use flat delivery columns first, fallback to invoice columns
    if (customer.delivery_line1) {
      return formatAddressFromFlat(customer, 'delivery_', { separator: '\n' });
    }
    if (customer.invoice_line1) {
      return formatAddressFromFlat(customer, 'invoice_', { separator: '\n' });
    }

    // Fall back to main address field
    if (customer.address) {
      return formatAddressForEdit(customer.address);
    }

    return '';
  };

  const loadPOFromOrder = async (orderId, items = products) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/po-data`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success && data.order) {
        const order = data.order;

        // The order's own supplier terms (there is no default any more), and ours.
        if (order.supplier_delivery_terms) setSupplierDeliveryTerms(order.supplier_delivery_terms);
        setOurDeliveryTerms(order.excel_agri_delivery_terms || '');
        setLinkedOrdersTransport([{ supplier_delivery_terms: order.supplier_delivery_terms || null,
          excel_agri_delivery_terms: order.excel_agri_delivery_terms || null,
          delivery_date: order.delivery_date ? String(order.delivery_date).slice(0, 10) : null }]);

        // Set supplier (suggested from contract)
        if (order.supplier) {
          setSelectedSupplierId(order.supplier.id.toString());
          // Parse address - handle JSON string, object, or plain text
          let supplierAddr = order.supplier.address || '';
          if (typeof supplierAddr === 'string' && supplierAddr.startsWith('{')) {
            try {
              supplierAddr = JSON.parse(supplierAddr);
            } catch (e) {
              // Not valid JSON, use as-is
            }
          }
          setSupplierAddress(formatAddressForEdit(supplierAddr));
          setSupplierPhone(order.supplier.phone || '');
          setSupplierEmail(order.supplier.email || '');
        } else {
          setAlertDialog({
            isOpen: true,
            message: 'No supplier found for this order. Please select a supplier.',
            type: 'warning'
          });
        }

        // Set customer (link to customer for this order)
        if (order.customer && order.customer.id) {
          setSelectedCustomerId(order.customer.id.toString());
        }

        // Set dates and delivery info
        setPODate(order.date ? order.date.split('T')[0] : new Date().toISOString().split('T')[0]);
        setDeliveryDate(order.delivery_date ? order.delivery_date.split('T')[0] : '');
        setRawDeliveryAddress(order.delivery_address);
        setDeliveryAddress(formatAddressForEdit(order.delivery_address));
        setDeliveryInstructions(order.delivery_instructions || '');
        setReference(order.reference || '');

        // Load line items, preserving each one's link to its customer order line
        const fromOrder = poLinesFromApi(
          (order.line_items || []).map(item => ({ ...item, orderLineId: item.orderLineId || item.id || null })),
          items
        );
        setLineItems(fromOrder.length ? fromOrder : [blankLine()]);
      }
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        message: `Failed to load order data: ${error.message}`,
        type: 'error'
      });
    }
    setLoading(false);
  };

  const loadPOForEdit = async (poId, productList = products, supplierList = suppliers, pending = null) => {
    setLoading(true);
    try {
      const data = await (pending || fetch(`${API_URL}/api/purchase-orders/${poId}`, {
        credentials: 'include'
      }).then(r => r.json()));

      if (!data?.success || !data.purchaseOrder) {
        setAlertDialog({
          isOpen: true,
          message: `Couldn't load the purchase order${data?.error ? `: ${data.error}` : ' — it was not found'}.`,
          type: 'error'
        });
      } else {
        const po = data.purchaseOrder;

        // Set supplier - try by ID first, then by Xero contact ID, then by name
        if (po.supplierId) {
          setSelectedSupplierId(po.supplierId.toString());
        } else if (po.xeroContactId) {
          const supplier = supplierList.find(s => s.xero_contact_id === po.xeroContactId);
          if (supplier) {
            setSelectedSupplierId(supplier.id.toString());
          }
        } else if (po.contactName) {
          const supplier = supplierList.find(s => s.company_name === po.contactName);
          if (supplier) {
            setSelectedSupplierId(supplier.id.toString());
          }
        }

        // Load supplier contact details
        setSupplierAddress(formatAddressForEdit(po.supplierAddress));
        setSupplierPhone(po.supplierPhone || '');
        setSupplierEmail(po.supplierEmail || '');

        // Set customer - try by ID from customer order link or address match
        loadedCustomerIdRef.current = po.customerId ? po.customerId.toString() : '';
        if (po.customerId) {
          setSelectedCustomerId(po.customerId.toString());
        }
        setLinkedOrderNumber(po.customerOrderNumber || null);


        setPODate(po.date ? po.date.split('T')[0] : new Date().toISOString().split('T')[0]);
        setDeliveryDate(po.deliveryDate ? po.deliveryDate.split('T')[0] : '');
        setRawDeliveryAddress(po.deliveryAddress);
        setDeliveryAddress(formatAddressForEdit(po.deliveryAddress));
        setDeliveryInstructions(po.deliveryInstructions || '');
        setSupplierDeliveryTerms(po.supplierDeliveryTerms || po.deliveryTerms || 'Delivery');
        setOurDeliveryTerms(po.customerOrderOurTerms || '');
        setReference(po.reference || '');
        setSupplierReference(po.supplierReference || '');
        // What it was when it opened, so saving can tell a reference that has
        // just been ADDED from one that was already there.
        loadedSupplierRefRef.current = po.supplierReference || '';
        // Older local POs were written in title case; the dropdown speaks the
        // uppercase vocabulary synced POs already use.
        setStatus(normalisePoStatus(po.status) || DEFAULT_PO_STATUS);
        setLoadedStatus(normalisePoStatus(po.status) || null);
        setPoNumber(po.purchaseOrderNumber || '');
        setPoAfterDelivery(po.afterDelivery || null);
        setCorrectionReason('');
        // Self-billed: where its goods go, and OUR terms (kept on the PO itself).
        setSelfBilled(!!po.selfBilled);
        setSelfBilledReady(!!po.selfBilledReady);
        setSelfBilledServerBlock(po.selfBilledBlock || null);
        setDeliveryTarget(po.selfBilled
          ? (po.deliveryTo === DELIVERY_TO_STOCK ? STOCK_OPTION_VALUE : (po.deliveryContactId ? String(po.deliveryContactId) : ''))
          : '');
        if (po.selfBilled) setOurDeliveryTerms(po.ourDeliveryTerms || '');
        setSelfBilledHaulageReady(!!po.selfBilledHaulageReady);
        setSelfBilledHaulierId(po.haulierAccountId ? String(po.haulierAccountId) : '');
        // A self-billed or stock PO's own delivery service (an order's PO-D has none).
        setSelfBilledHaulageService(po.haulageService || STANDARD_HAULAGE_SERVICE);
        setHaulageStandardReady(po.haulageStandardReady !== false);
        setSelfBilledHaulagePo(po.selfBilledHaulagePo || null);
        setLinkedOrdersTransport(Array.isArray(po.linkedOrdersTransport) ? po.linkedOrdersTransport : []);
        setLoadedDeliveryDate(po.deliveryDate ? String(po.deliveryDate).slice(0, 10) : null);

        // Load line items. orderLineId is kept for the sync back to the
        // customer order, and every field the grid does not show rides along
        // in the line's _source so a save writes it back.
        const items = Array.isArray(po.lineItems) ? po.lineItems : (po.lineItems ? JSON.parse(po.lineItems) : []);
        const loaded = poLinesFromApi(items, productList);
        setLineItems(loaded.length ? loaded : [blankLine()]);
      }
    } catch (error) {
      setAlertDialog({
        isOpen: true,
        message: 'Failed to load purchase order: ' + error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };


  /*
   * Saving an edit that ADDS a supplier's reference.
   *
   * A supplier quoting their own reference back is them acknowledging the
   * order, which is what Confirmed means — so the status almost always wants
   * moving with it, and doing that as a second trip through the editor is the
   * step people forget. Offered, never assumed: a reference can be typed in
   * from a quote long before anyone has agreed anything.
   *
   * Only when it goes from EMPTY to something, and only on an edit. Changing a
   * reference that was already there says nothing new about the status.
   */
  const supplierRefJustAdded = () =>
    editMode
    && !String(loadedSupplierRefRef.current || '').trim()
    && !!String(supplierReference || '').trim()
    && String(status || '').toUpperCase() !== 'CONFIRMED';

  // A PO raised for a customer order: its terms follow the order's.
  const raisedFromOrder = editMode ? !!linkedOrderNumber : !!creatingFromOrderId;
  /*
   * Transport (haulier, driver, vehicle) for a delivery today or later is the
   * planning board's to set — read-only here for everyone, admins included,
   * and ignored by the server if sent. Once the day has passed it can be
   * corrected here. The dates are the PO's as loaded (or as typed on a new
   * one) and its customer order's. A PO with NO customer order is never on
   * the boards, so its transport is always set here (Cam, 2026-10-12) — the
   * server's rule too (utils/poTransportLock).
   */
  const forCustomerOrder = poHasCustomerOrder({
    customerOrderId: raisedFromOrder ? (linkedOrderNumber || creatingFromOrderId) : null,
    lines: lineItems
  });
  const transportRuleNow = poTransportRule({
    selfBilled,
    supplierTerms: supplierDeliveryTerms,
    ourTerms: ourDeliveryTerms,
    linkedOrders: raisedFromOrder || forCustomerOrder ? linkedOrdersTransport : [],
    poDeliveryDate: [loadedDeliveryDate, deliveryDate],
    today: londonToday()
  });
  // Read-only, with the reason: the board's until the day after delivery,
  // then a correction for Edit Transport Planning holders. Customer
  // Collection is the fixed label below.
  const transportLockedReason = transportRuleNow.phase === TRANSPORT_PHASE_BOARD || transportRuleNow.phase === TRANSPORT_PHASE_CORRECTION
    ? transportChangeRefusal(transportRuleNow, { path: PATH_PO_EDITOR, canPlan: hasPermission(user, 'transport_planning') })
    : null;
  // Customer Collection: the transport cells read the label and are fixed.
  // A self-billed PO has its own two sides (ours is kept on the PO).
  const transportFixedLabel = (raisedFromOrder || selfBilled) && isCustomerCollection(supplierDeliveryTerms, ourDeliveryTerms)
    ? CUSTOMER_COLLECTION_LABEL : null;

  /*
   * SELF-BILLED. Why the switch cannot be changed: the server's reason on an
   * edit (worked out on the PO as it is stored), else the same rule here — a
   * PO raised from an order can never be self-billed.
   */
  const selfBilledBlock = editMode
    ? (selfBilledReady ? selfBilledServerBlock : selfBilledToggleBlock({ ready: false }))
    : selfBilledToggleBlock({ ready: selfBilledReady, on: selfBilled, forCustomerOrder: raisedFromOrder || forCustomerOrder });
  // Collected by a third party: its haulier is named here (and gets a PO-H).
  const selfBilledThirdParty = selfBilled && selfBilledHaulageReady && ourDeliveryTerms === THIRD_PARTY_OUR_TERMS;
  /*
   * A STOCK PO collected by a third party (utils/stockHaulage, Cam
   * 2026-10-29): no order, no customer, not self-billed, on Third-Party
   * Collection — it names its haulier too, and gets a PO-H to our site. A PO
   * naming a customer (or for an order) on those terms says why it does not.
   */
  const stockHaulageArgs = {
    selfBilled, forCustomerOrder: raisedFromOrder || forCustomerOrder,
    customerId: selectedCustomerId, supplierTerms: supplierDeliveryTerms
  };
  const stockHaulageReady = selfBilledReady && selfBilledHaulageReady;
  const stockTP = stockHaulageReady && stockThirdParty(stockHaulageArgs);
  // Said only for a PO naming a customer: an order's PO-D on these terms is the
  // order's (its supplier terms already say so), and says nothing new here.
  const stockHaulageReason = stockHaulageReady && !(raisedFromOrder || forCustomerOrder)
    ? stockHaulageBlock(stockHaulageArgs) : null;
  // Either kind: the Third-party haulier and Delivery service fields, and pallets on packed lines.
  const haulageThirdParty = selfBilledThirdParty || stockTP;
  // Who collects it (a supplier contact, as the order screen's Third-Party
  // Haulier) and the service they are booked for — one block for both kinds.
  const haulageFields = haulageThirdParty ? (
    <>
      <div className="form-group po-self-billed-haulier" title={stockTP ? STOCK_HAULAGE_HINT : undefined}>
        <label>Third-party haulier <span className='required-field'>*</span></label>
        <SearchableDropdown
          value={selfBilledHaulierId}
          onChange={(v) => setSelfBilledHaulierId(v || '')}
          options={suppliers.map(sp => ({
            value: String(sp.id), badge: sp.account_no || ' ', label: sp.company_name,
            searchText: `${sp.account_no || ''} ${sp.company_name}`
          }))}
          placeholder="Who collects it?"
          placeholderClassName={selfBilledHaulierId ? '' : 'sd-placeholder-error'}
          searchPlaceholder="Search hauliers by # or name..."
        />
        <span className="po-self-billed-haulage-po">
          {selfBilledHaulagePo
            ? `Haulage order ${selfBilledHaulagePo.purchase_order_number} (${selfBilledHaulagePo.haulier || 'haulier'}) follows this PO when it is saved.`
            : stockTP
              ? 'A haulage order (PO-H) to the haulier — collect from the supplier, deliver to our site — is raised when this PO is saved.'
              : 'A haulage order (PO-H) to the haulier is raised when this PO is saved.'}
        </span>
      </div>
      <div className="form-group po-self-billed-service"
        title={(HAULAGE_SERVICE_OPTIONS.find(o => o.value === selfBilledHaulageService) || {}).hint || ''}>
        <label>Delivery service</label>
        <Dropdown
          value={selfBilledHaulageService}
          onChange={(v) => setSelfBilledHaulageService(v || STANDARD_HAULAGE_SERVICE)}
          options={HAULAGE_SERVICE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          placeholder="Select service..."
        />
        {selfBilledHaulageService === STANDARD_HAULAGE_SERVICE && !haulageStandardReady && (
          <span className="po-self-billed-haulage-po po-service-not-ready">
            Standard needs a database update that has not been run yet — until then the PO and its PO-H are saved with no delivery service.
          </span>
        )}
      </div>
    </>
  ) : null;
  const deliveryContact = selfBilled && deliveryTarget && deliveryTarget !== STOCK_OPTION_VALUE
    ? (allContacts || []).find(c => String(c.id) === String(deliveryTarget)) : null;
  // What a self-billed PO will print as where its goods go (the server works
  // it out again from the contact when it saves).
  const selfBilledAddressText = !selfBilled || !deliveryTarget ? ''
    : deliveryTarget === STOCK_OPTION_VALUE ? stockAddressText() : contactDeliveryText(deliveryContact);

  // Third-Party pairs on a self-billed PO's own two sides (an order's are paired on the order).
  const pairSelfBilled = (side, value, next) => (selfBilled ? pairForSelfBilled(side, value, next) : next);

  const handleSelfBilledChange = (on) => {
    setSelfBilled(on);
    if (on) {
      // Ours: no customer — "None — delivered to us".
      setSelectedCustomerId('');
      // Our terms start as what the supplier's imply, when they imply one.
      if (!ourDeliveryTerms) {
        setOurDeliveryTerms(supplierDeliveryTerms === THIRD_PARTY_SUPPLIER_TERMS ? THIRD_PARTY_OUR_TERMS
          : supplierDeliveryTerms === CUSTOMER_COLLECTION_LABEL ? CUSTOMER_COLLECTION_LABEL : 'Delivery');
      }
    } else {
      setDeliveryTarget('');
      if (!raisedFromOrder) setOurDeliveryTerms('');
      setRawDeliveryAddress(null);
      setDeliveryAddress(stockAddressText());
    }
  };

  const closeConfirm = () => setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
  /*
   * Taking an archived PO off Archived — asked once, whichever way the new
   * status came (Cam, 2026-10-15). It is unusual: the PO goes live again, but
   * the order lines taken off it when it was archived stay off (Restore on
   * Search POs re-links them). No keeps it Archived and saves.
   */
  const askLeaveArchive = (target, opts) => {
    const label = poStatusLabel(target);
    setConfirmDialog({
      isOpen: true,
      type: 'warning',
      title: `Take ${poNumber || 'this PO'} out of the archive?`,
      message: `${poNumber || 'This PO'} is archived. Saving it as ${label} makes it a live purchase order again, which is unusual for an archived PO. `
        + 'Order lines taken off it when it was archived are not linked back — Restore on Search Purchase Orders does that. Are you sure?',
      confirmText: `Yes, set ${label}`,
      cancelText: 'No, keep Archived',
      onConfirm: () => {
        closeConfirm();
        setStatus(target);
        handleSavePO({ ...opts, statusChecked: true, unarchiveChecked: true, statusOverride: target });
      },
      onCancel: () => {
        closeConfirm();
        setStatus(loadedStatus);
        handleSavePO({ ...opts, statusChecked: true, unarchiveChecked: true, statusOverride: loadedStatus });
      }
    });
  };

  /*
   * A typed line names its account (2.25.0 — utils/lineAccountRequired): a new
   * line, or one whose item, words, quantity or price changed since the PO was
   * opened (its `_source`). A line bought for an order line is the order's.
   */
  const accountRefusals = documentLineAccountRefusals(lineItems.filter(l => !lineIsEmpty(l)), {
    before: (l) => l._source,
    exempt: (l) => !!l.orderLineId
  });
  const accountRefusalText = accountRefusalMessage(accountRefusals, 'po');

  const handleSavePO = async (opts = {}) => {
    const archivedLoaded = editMode && isArchivedPoStatus(loadedStatus);
    // An archived PO given a supplier reference: kept for reference, still
    // Archived — the default. Confirmed is the other choice, and asks again.
    if (!opts.statusChecked && archivedLoaded && isArchivedPoStatus(status) && supplierRefJustAdded()) {
      setConfirmDialog({
        isOpen: true,
        type: 'info',
        title: 'Keep it archived?',
        message: `${poNumber || 'This PO'} is archived. The supplier's reference (${supplierReference.trim()}) will be saved with it for reference, and it stays Archived. `
          + 'The box saves it and keeps it Archived; the circled tick sets it Confirmed instead (you will be asked once more); × goes back to the PO.',
        confirmText: 'Save, keep Archived',
        confirmIcon: 'inventory_2',
        secondaryText: 'Set Confirmed…',
        secondaryIcon: 'task_alt',
        cancelText: 'Back to the PO',
        onConfirm: () => { closeConfirm(); handleSavePO({ ...opts, statusChecked: true, unarchiveChecked: true }); },
        onSecondary: () => askLeaveArchive('CONFIRMED', opts),
        onCancel: closeConfirm
      });
      return;
    }
    // After delivery the status moves forward only: the question is asked
    // when Confirmed is still a forward move (it never is from Billed).
    if (!opts.statusChecked && supplierRefJustAdded()
        && (!poAfterDelivery || !statusMoveAfterDelivery(loadedStatus, 'CONFIRMED'))) {
      setConfirmDialog({
        isOpen: true,
        type: 'info',
        title: 'Mark as confirmed?',
        message: `You've added the supplier's reference (${supplierReference.trim()}). That usually means they have acknowledged the order — set the status to Confirmed as well?`,
        confirmText: 'Yes, set Confirmed',
        cancelText: `No, leave as ${status}`,
        onConfirm: () => {
          setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
          setStatus('CONFIRMED');
          // Passed explicitly rather than read from state: setStatus has not
          // landed by the time this runs.
          handleSavePO({ statusChecked: true, statusOverride: 'CONFIRMED' });
        },
        onCancel: () => {
          setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
          handleSavePO({ statusChecked: true });
        }
      });
      return;
    }
    // Leaving Archived by any route (the Confirmed question above, or the
    // status picked by hand): asked once.
    if (!opts.unarchiveChecked && archivedLoaded && !isArchivedPoStatus(opts.statusOverride || status)) {
      askLeaveArchive(normalisePoStatus(opts.statusOverride || status), opts);
      return;
    }

    // Validation
    if (!selectedSupplierId) {
      setAlertDialog({
        isOpen: true,
        message: 'Please select a supplier',
        type: 'warning'
      });
      return;
    }
    if (!supplierDeliveryTerms) {
      setAlertDialog({ isOpen: true, message: 'Choose the delivery terms', type: 'warning' });
      return;
    }
    // Self-billed: where the goods go, and both sides' terms (the server checks too).
    if (selfBilled) {
      if (!deliveryTarget) {
        setAlertDialog({ isOpen: true, message: ADDRESS_REQUIRED_MESSAGE, type: 'warning' });
        return;
      }
      const termsProblem = selfBilledTermsProblem(supplierDeliveryTerms, ourDeliveryTerms);
      if (termsProblem) {
        setAlertDialog({ isOpen: true, message: termsProblem, type: 'warning' });
        return;
      }
      if (selfBilledThirdParty && !selfBilledHaulierId) {
        setAlertDialog({ isOpen: true, message: 'Choose the third-party haulier who collects it — they are sent a haulage order (PO-H).', type: 'warning' });
        return;
      }
    }
    // A stock PO collected by a third party names its haulier too (the server checks too).
    if (stockTP && !selfBilledHaulierId) {
      setAlertDialog({ isOpen: true, message: STOCK_HAULIER_REQUIRED_MESSAGE, type: 'warning' });
      return;
    }

    // A line must name something — an item or a description.
    const realLines = lineItems.filter(l => !lineIsEmpty(l));
    const blanks = lineItems.length - realLines.length;
    if (realLines.length === 0) {
      setAlertDialog({
        isOpen: true,
        message: 'Add at least one line with an item or a description.',
        type: 'warning'
      });
      return;
    }
    if (blanks > 0) {
      setAlertDialog({
        isOpen: true,
        type: 'warning',
        message: `${blanks} line${blanks > 1 ? 's have' : ' has'} neither an item nor a description. Fill them in, or remove them, then save again.`
      });
      return;
    }
    // A typed line names its purchase account (2.25.0).
    if (accountRefusals.length) {
      setAlertDialog({ isOpen: true, type: 'warning', message: accountRefusalText });
      return;
    }
    // Collected by a third-party haulier (self-billed or stock): booked by the
    // pallet, so a bagged or packed line needs its pallets — typed, or from a
    // qty per pallet (utils/lineUnits; the server refuses it too).
    if (haulageThirdParty) {
      const noPallets = linesMissingPallets(realLines, line => (line.inventory_item_id
        ? products.find(i => i.id === line.inventory_item_id) : null)
        || products.find(i => line.product_code && String(i.item_code || '').trim().toLowerCase() === String(line.product_code).trim().toLowerCase())
        || null);
      if (noPallets.length > 0) {
        const named = noPallets.map(line => line.product_code || line.product_name).filter(Boolean);
        setAlertDialog({
          isOpen: true,
          type: 'warning',
          message: 'Enter the Pallets for every bagged or packed line — the haulier is booked by the pallet.'
            + (named.length ? ` Missing on: ${named.join(', ')}.` : '')
        });
        return;
      }
    }

    setLoading(true);

    try {
      // Get supplier details from selected supplier
      const supplier = suppliers.find(s => s.id.toString() === selectedSupplierId);
      if (!supplier) {
        throw new Error('Selected supplier not found');
      }
      const supplierName = supplier.company_name;
      const xeroContactId = supplier.xero_contact_id || null;

      const url = editMode
        ? `${API_URL}/api/purchase-orders/${editingPOId}`
        : `${API_URL}/api/purchase-orders`;

      const method = editMode ? 'PUT' : 'POST';

      // Get customer name if a customer is selected
      const customer = selectedCustomerId ? customers.find(c => c.id.toString() === selectedCustomerId) : null;
      const customerName = customer?.company_name || null;

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          xero_contact_id: xeroContactId,
          contact_name: supplierName,
          supplier_address: supplierAddress || null,
          supplier_phone: supplierPhone || null,
          supplier_email: supplierEmail || null,
          account_id: selectedSupplierId ? parseInt(selectedSupplierId) : null, // Supplier account ID
          date: poDate,
          delivery_date: deliveryDate || null,
          delivery_address: (rawDeliveryAddress && deliveryAddress === formatAddressForEdit(rawDeliveryAddress)
            ? rawDeliveryAddress : deliveryAddress) || null,
          delivery_instructions: deliveryInstructions || null,
          supplier_delivery_terms: supplierDeliveryTerms,
          // Ours, for a PO raised from an order — written through to it.
          ...(raisedFromOrder && ourDeliveryTerms ? { excel_agri_delivery_terms: ourDeliveryTerms } : {}),
          // Self-billed (only once the database can hold it): where the goods
          // go, and our terms, kept on the PO. Sent as off too, so an edit can
          // switch it off.
          ...(selfBilledReady ? {
            self_billed: selfBilled,
            ...(selfBilled ? {
              delivery_to: deliveryTarget === STOCK_OPTION_VALUE ? DELIVERY_TO_STOCK : DELIVERY_TO_CONTACT,
              delivery_contact_id: deliveryTarget === STOCK_OPTION_VALUE ? null : parseInt(deliveryTarget, 10),
              excel_agri_delivery_terms: ourDeliveryTerms,
              ...(selfBilledHaulageReady ? {
                haulier_account_id: selfBilledThirdParty && selfBilledHaulierId ? parseInt(selfBilledHaulierId, 10) : null,
                haulage_service: selfBilledThirdParty ? (selfBilledHaulageService || STANDARD_HAULAGE_SERVICE) : null
              } : {})
            } : {})
          } : {}),
          // A stock PO collected by a third party: its haulier and delivery
          // service (the server stores our side as Third-Party Delivery).
          ...(stockTP ? {
            haulier_account_id: selfBilledHaulierId ? parseInt(selfBilledHaulierId, 10) : null,
            haulage_service: selfBilledHaulageService || STANDARD_HAULAGE_SERVICE
          } : {}),
          // Yes to "clear the haulier, driver and vehicle" (Customer Collection).
          ...(opts.confirmCustomerCollection ? { confirm_customer_collection: true } : {}),
          // A PO made in Xero, changed there since it was read: send ours over it (2026-10-22).
          ...(opts.xeroNewerConfirmed ? { xero_newer_confirmed: opts.xeroNewerConfirmed } : {}),
          // Why a billed PO is being changed (PostedEditDialog).
          // A correction after delivery may carry a reason (optional — kept in the history).
          ...(opts.editReason ? { edit_reason: opts.editReason }
            : (poAfterDelivery && correctionReason.trim() ? { edit_reason: correctionReason.trim() } : {})),
          customer_name: selfBilled ? null : customerName,
          customer_id: !selfBilled && selectedCustomerId ? parseInt(selectedCustomerId) : null,
          ...(editMode && loadedCustomerIdRef.current ? { loaded_customer_id: parseInt(loadedCustomerIdRef.current, 10) } : {}),
          reference: reference || null,
          supplier_reference: supplierReference || null,
          // The override, when the confirm dialog above has just chosen one:
          // setStatus has not landed by the time this runs.
          status: opts.statusOverride || status,
          // Net line amounts, additives included, and the fields the grid
          // does not show carried through — see poLinesToPayload.
          // The Customer Collection label is a reading, never a value: a cell
          // still showing it (terms just changed off it) is sent as blank.
          line_items: poLinesToPayload(realLines.map(l => ({
            ...l,
            ...Object.fromEntries(['haulier', 'driver', 'vehicle']
              .filter(f => l[f] === CUSTOMER_COLLECTION_LABEL).map(f => [f, ''])),
          })), lineTotals).map((li, i) => (realLines[i]._source
            // What this line showed when the PO was opened: a field left as it
            // was keeps whatever a planning board has set since (server-side).
            ? { ...li, _loadedTransport: Object.fromEntries(['haulier', 'driver', 'vehicle']
              .map(f => [f, realLines[i]._source[f] || null])) }
            : li))
        })
      });

      const result = await response.json();
      if (result.code !== 'EDIT_REASON_REQUIRED') setReasonPrompt(null);

      if (response.ok && result.success) {
        // If this PO was created from a customer order, update the order status
        if (!editMode && creatingFromOrderId && reference && reference.startsWith('SO-')) {
          try {
            await fetch(`${API_URL}/api/orders/customer-orders/${creatingFromOrderId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ status: 'PO Created' })
            });
          } catch (error) {
            console.error('Failed to update order status:', error);
          }
        }

        if (editMode) {
          const showDone = () => setAlertDialog({
            isOpen: true,
            message: result.message || 'Purchase order updated successfully',
            // A note to act on (a billed PO keeps the old item on its bill, a unit kept), or a
            // moved date that cleared the old day's haulier / driver / vehicle (in the message): a warning.
            type: ((result.warnings || []).length || result.transport_cleared) ? 'warning' : 'success',
            onCloseCallback: () => {
              resetForm();
              if (onNavigate) {
                onNavigate('search-purchase-orders');
              }
            }
          });
          /*
           * A changed customer reaches this PO, its order's other POs and the
           * PO-H. Any a supplier or haulier already holds are offered as a
           * revised order, saying what changed — as an order edit does.
           */
          const changed = Array.isArray(result.po_changes) ? result.po_changes : [];
          const refreshed = Array.isArray(result.haulage_refreshed) ? result.haulage_refreshed : [];
          // A PO-H already with its haulier (an order's, or a self-billed
          // PO's own) is offered as a revised order too.
          const revised = [
            ...changed.filter(p => p.sent_to_contact),
            ...refreshed.filter(p => p.sent_to_contact && p.outcome === 'updated')
          ];
          // A PO-H its haulier holds that this save archived (the haulier
          // changed, or the PO left third-party terms): offer the cancellation.
          const cancelled = refreshed.filter(p => p.sent_to_contact && p.outcome === 'cancel');
          const afterInvoice = () => {
          if (revised.length || cancelled.length) {
            const base = (p) => ({
              id: p.id,
              purchase_order_number: p.purchase_order_number,
              xero_purchase_order_id: p.xero_purchase_order_id,
              po_type: p.po_type,
              supplier_name: p.supplier_name,
              sent_to_contact: p.sent_to_contact
            });
            setRevisedPrompt({
              pos: [
                ...revised.map(p => ({ ...base(p), revision: { summary: 'amended', changes: (p.changes || []).filter(Boolean) } })),
                ...cancelled.map(p => ({ ...base(p), cancellation: { reason: (p.changes || []).filter(Boolean).join('; ') || 'purchase order changed' } }))
              ],
              message: [
                revised.length ? revised.map(p => `${p.purchase_order_number} (${p.supplier_name || 'supplier'}): ${(p.changes || []).join('; ') || 'updated'}.`).join(' ')
                  + ` ${revised.length === 1 ? 'It has' : 'They have'} the previous version.` : '',
                cancelled.length ? cancelled.map(p => `${p.purchase_order_number} (${p.supplier_name || 'haulier'}) was archived: ${(p.changes || []).join('; ') || 'no longer needed'}.`).join(' ')
                  + ` ${cancelled.length === 1 ? 'Its haulier has' : 'Their hauliers have'} it.` : ''
              ].filter(Boolean).join(' ')
                + (cancelled.length && revised.length ? ' Send the revised orders and the cancellations now?'
                  : cancelled.length ? ` Send the cancellation${cancelled.length === 1 ? '' : 's'} now?`
                  : ' Send the revised one now?'),
              // Then a PO-H this save newly raised (a new haulier's) — offered to send.
              next: () => offerRaisedHaulage(result.haulage_raised, showDone)
            });
          } else {
            offerRaisedHaulage(result.haulage_raised, showDone);
          }
          };
          // An item code that reached an invoiced order: offer its invoice the change.
          if (hasInvoiceOffer(result.invoice_offer) && result.invoice_offer_order_id) {
            setInvoiceOffer({
              offer: result.invoice_offer,
              orderId: result.invoice_offer_order_id,
              reason: opts.editReason || correctionReason.trim() || '',
              next: afterInvoice
            });
          } else {
            afterInvoice();
          }
        } else {
          // Offer to send it straight away — the next thing done with a new
          // PO, and one step here rather than finding it again in the list.
          // A PO-H raised for it (collected by a third party) is offered first;
          // the window then says whether it went (2026-10-29).
          const raisedHaulage = (Array.isArray(result.haulage_raised) ? result.haulage_raised : []).filter(h => h && !h.sent_to_contact);
          offerRaisedHaulage(raisedHaulage, (haulageSent) => setPostSaveDialog({
            isOpen: true,
            poNumber: result.po_number,
            poId: result.po_id,
            xeroId: result.xero_purchase_order_id,
            supplier: supplierName,
            sent: false,
            haulage: raisedHaulage.length
              ? { numbers: raisedHaulage.map(h => h.purchase_order_number).join(', '), haulier: raisedHaulage[0].haulier, sent: !!haulageSent }
              : null
          }));
        }
      } else if (result.code === 'EDIT_REASON_REQUIRED') {
        // A billed PO: ask why, then save again with the answer.
        // (opts is the click event on a first save — keep only real options.)
        setReasonPrompt({
          opts: { statusOverride: opts.statusOverride, confirmCustomerCollection: opts.confirmCustomerCollection, xeroNewerConfirmed: opts.xeroNewerConfirmed },
          message: result.error
        });
      } else if (result.code === 'XERO_PO_NEWER' && result.details?.xero_newer) {
        // Made in Xero and changed there since this PO was read: ours goes
        // over Xero's only if the person says so (services/xeroPoLineSyncService).
        const info = result.details.xero_newer;
        setConfirmDialog({
          isOpen: true,
          type: 'warning',
          title: `${info.purchase_order_number || 'This PO'} was changed in Xero`,
          message: result.error,
          confirmText: 'Send ours to Xero',
          cancelText: 'Go back',
          onConfirm: () => {
            setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
            handleSavePO({ ...opts, statusChecked: true, xeroNewerConfirmed: info.xero_updated });
          }
        });
      } else if (result.code === 'CUSTOMER_COLLECTION_TRANSPORT') {
        // As on the order screen: Yes clears and saves, No saves nothing and
        // leaves the editor as it was. The server refuses without the answer.
        setConfirmDialog({
          isOpen: true,
          type: 'warning',
          title: 'Clear the haulier, driver and vehicle?',
          message: `${result.error} Clear them and save?`,
          confirmText: 'Yes, clear and save',
          cancelText: 'No, go back',
          onConfirm: () => {
            setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
            handleSavePO({ ...opts, statusChecked: true, confirmCustomerCollection: true });
          }
        });
      } else {
        throw new Error(result.error || 'Failed to save purchase order');
      }
    } catch (error) {
      setReasonPrompt(null);
      setAlertDialog({
        isOpen: true,
        message: 'Failed to save purchase order: ' + error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setEditingPOId(null);
    setSelectedSupplierId('');
    setSupplierAddress('');
    setSupplierPhone('');
    setSupplierEmail('');
    setSelectedCustomerId('');
    setLinkedOrderNumber(null);
    setPODate(new Date().toISOString().split('T')[0]);
    setDeliveryDate('');
    setDeliveryAddress('Excel Agri Ltd.\nThe Old Grain Drier\nDolton Beacon\nWinkleigh\nDevon\nEX19 8PS');
    setRawDeliveryAddress(null);
    setDeliveryInstructions('');
    setSupplierDeliveryTerms('');
    setOurDeliveryTerms('');
    setReference('');
    setSupplierReference('');
    setStatus(DEFAULT_PO_STATUS);
    setLoadedStatus(null);
    setPoNumber('');
    setPoAfterDelivery(null);
    setCorrectionReason('');
    setInvoiceOffer(null);
    setSelfBilled(false);
    setSelfBilledServerBlock(null);
    setDeliveryTarget('');
    setSelfBilledHaulierId('');
    setSelfBilledHaulageService(STANDARD_HAULAGE_SERVICE);
    setSelfBilledHaulagePo(null);
    setLinkedOrdersTransport([]);
    setLoadedDeliveryDate(null);
    setLineItems([blankLine()]);
    loadNextPoNumber();
  };

  // A line's item (the PO's list carries each item's supplier — item_supplier).
  const lineItemOf = (line) => (line?.inventory_item_id
    ? products.find(p => p.id === line.inventory_item_id) || null : null);

  /*
   * An item picked on a line (Cam, 2026-11-02): the Supplier is filled only
   * when none is chosen yet and every line's item names the same ONE supplier
   * — never a supplier group's (its depots are pinned; the person picks).
   * The lines are the ones on screen with the picked item in its row (the
   * grid's change has not reached state yet).
   */
  const fillSupplierFromItems = (item, index) => {
    if (selectedSupplierId) return;
    const items = lineItems.map((l, i) => (i === index ? item : lineItemOf(l)));
    const id = headerSupplierFill(items, { current: selectedSupplierId, offered: suppliers });
    if (id) handleSupplierChange(String(id));
  };

  // Choosing a supplier fills in its address, phone and email — on an edit too.
  // Those fields are no longer on screen, so there is nowhere to offer an
  // "update to the new supplier's address" choice; a PO whose supplier changed
  // but kept the old supplier's address would print to the wrong company.
  const handleSupplierChange = (value, list = suppliers) => {
    setSelectedSupplierId(value);
    if (value) {
      const supplier = list.find(s => s.id.toString() === value);
      if (supplier) {
        setSupplierAddress(contactAddressText(supplier.address));
        setSupplierPhone(supplier.phone || '');
        setSupplierEmail(supplier.email || '');
      }
    } else if (!editMode) {
      setSupplierAddress('');
      setSupplierPhone('');
      setSupplierEmail('');
    }
  };

  /*
   * Choosing a customer. A new PO's address is filled by the effect above; on
   * an edit it is filled here, so loading the PO never overwrites the address
   * it was saved with. A PO for a customer order cannot be left with none.
   */
  const handleCustomerChange = (value) => {
    if (!value && editMode && linkedOrderNumber) return;
    setSelectedCustomerId(value);
    if (!editMode) return;
    const customer = value ? customers.find(c => c.id.toString() === value) : null;
    setRawDeliveryAddress(null);
    setDeliveryAddress(customer
      ? (getCustomerDeliveryAddress(customer) || '')
      : 'Excel Agri Ltd.\nThe Old Grain Drier\nDolton Beacon\nWinkleigh\nDevon\nEX19 8PS');
  };

  const handleCancel = () => {
    if (onNavigate) {
      onNavigate('search-purchase-orders');
    }
  };

  // Reset under the lines. A new PO goes back to blank; an edit goes back to
  // the PO as it is saved, rather than turning into a new one.
  const handleReset = () => {
    setConfirmReset(false);
    if (editMode && editingPOId) loadPOForEdit(editingPOId);
    else resetForm();
  };

  // Always asks first.
  const requestReset = () => setConfirmReset(true);

  /*
   * A PO-H this save newly raised for the haulier (`haulage_raised` — never
   * sent): "PO-H… raised to <haulier> — send it now?" (2026-10-29). Send opens
   * the Send to Supplier window for it; either way `next(sent)` carries on.
   */
  const offerRaisedHaulage = (raised, next) => {
    const list = (Array.isArray(raised) ? raised : []).filter(h => h && !h.sent_to_contact);
    if (!list.length) { next(false); return; }
    haulageSentRef.current = false;
    const h = list[0];
    setHaulagePrompt({
      pos: [{
        id: h.id,
        purchase_order_number: h.purchase_order_number,
        // The send window previews the PDF by this — see CLAUDE.md on PurchaseOrderModal.
        xero_purchase_order_id: h.xero_purchase_order_id,
        po_type: 'haulage',
        sent_to_contact: false,
        supplier_name: h.haulier
      }],
      message: `${list.map(x => x.purchase_order_number).join(', ')} raised to ${h.haulier || 'the haulier'} to collect this purchase order — send it now?`,
      next
    });
  };

  const handleViewPO = () => {
    setPostSaveDialog({ ...postSaveDialog, isOpen: false });
    // The same handoff as every "Find in …" (utils/revealInSearch.js).
    if (!revealInSearch(onNavigate, 'search-purchase-orders', postSaveDialog.poNumber, null, { tab: 'orders' })) {
      onNavigate?.('search-purchase-orders');
    }
  };

  const handleSendToSupplier = () => {
    setPostSaveDialog(prev => ({ ...prev, isOpen: false }));
    setSendFlowPo({
      id: postSaveDialog.poId,
      purchase_order_number: postSaveDialog.poNumber,
      // PDF preview is fetched by this and nothing else — see CLAUDE.md.
      xero_purchase_order_id: postSaveDialog.xeroId,
      sent_to_contact: false,
      supplier_name: postSaveDialog.supplier
    });
  };

  const handleCreateAnother = () => {
    setPostSaveDialog({ ...postSaveDialog, isOpen: false });
    resetForm();
  };

  if (loading) {
    return (
      <div className="create-purchase-order">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="create-purchase-order document-form">

      <div className="page-header">
        <div className="workflow-info">
          <span className="info">{editMode ? 'Updates linked customer orders (SO-)' : 'Ordering products into stock'}</span>
        </div>
        <div className="header-actions">
          <button data-read-safe className="btn-round btn-danger" onClick={handleCancel}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">Cancel</span>
          </button>
        </div>
      </div>

      <div className="page-scroll-content">
      <div className="po-form-container">
        {/* PO Details */}
        <div className="form-section">
          <h2>PO Details</h2>

          <div className="po-details-row">
            <div className="form-group po-supplier-field">
              <div className="po-supplier-label-row">
                <label>Supplier <span className='required-field'>*</span></label>
                <button
                  type="button"
                  className="btn-full po-new-supplier"
                  onClick={() => setShowNewSupplier(true)}
                  data-write-action
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">add</span>
                  </div>
                  <span className="btn-label">New Supplier</span>
                </button>
              </div>
              <div className="doc-contact-pick">
                <div className="doc-contact-field">
                  <SearchableDropdown
                    value={selectedSupplierId}
                    onChange={(v) => handleSupplierChange(v)}
                    autoOpen={autoOpenSupplier}
                    // each line's item's supplier (or its group's depots) pinned first
                    options={pinSupplierOptions([
                      { value: '', label: 'Select supplier...' },
                      ...suppliers.map(s => withStoreTag(s, {
                        value: s.id.toString(),
                        label: s.company_name,
                        // Names on screen; account number and email still searchable.
                        searchText: `${s.company_name} ${s.account_no || ''} ${s.email || ''}`
                      }))
                    ], pinnedSupplierIds(lineItems.map(lineItemOf)))}
                    placeholder="Select supplier..."
                    searchPlaceholder="Type a supplier name..."
                  />
                </div>
                {selectedSupplierId && (
                  <button
                    type="button"
                    data-read-safe
                    className="btn-round view-contact"
                    onClick={() => setShowSupplierModal(true)}
                    title="View supplier details"
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">visibility</span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            <div className="form-group po-field-100">
              <label>Created Date <span className='required-field'>*</span></label>
              <DateInput
                className="styled-input"
                value={poDate}
                onChange={(e) => setPODate(e.target.value)}
                required
              />
            </div>

            <div className="form-group po-field-100">
              <label>Delivery Date</label>
              <div title={poAfterDelivery ? `${poAfterDelivery.reason} The delivery date stays as delivered.` : undefined}>
                <DateInput
                  className="styled-input"
                  value={deliveryDate}
                  disabled={!!poAfterDelivery}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
              </div>
            </div>

            <div
              className="form-group po-field-100"
              title={editMode ? undefined : 'Assigned when the PO is saved — this is the next free number'}
            >
              <label>PO #</label>
              <Input
                className="styled-input"
                type="text"
                value={editMode ? poNumber : nextPoNumber}
                placeholder="On save"
                readOnly
                disabled
              />
            </div>

            <div className="form-group po-field-120">
              <label>Supplier Ref</label>
              <Input
                className="styled-input"
                type="text"
                value={supplierReference}
                onChange={(e) => setSupplierReference(e.target.value)}
                placeholder="Mill SO no."
              />
            </div>

            {/* Was held, saved and never shown: every PO went out as whatever
                it was loaded with, or "Delivery" if it was new. */}
            <div
              className="form-group po-field-terms"
              title={raisedFromOrder ? 'Kept in step with the order. Direct Delivery and Third-Party are set on the order.' : undefined}
            >
              <label>Supplier Delivery Terms</label>
              <SearchableDropdown
                value={supplierDeliveryTerms}
                // Customer Collection on one side sets the other (pairOnChoose).
                onChange={(value) => {
                  const next = pairSelfBilled('supplier', value,
                    pairOnChoose('supplier', value, { supplier: supplierDeliveryTerms, ours: ourDeliveryTerms }));
                  setSupplierDeliveryTerms(next.supplier);
                  if ((raisedFromOrder || selfBilled) && next.ours !== ourDeliveryTerms) setOurDeliveryTerms(next.ours);
                }}
                options={raisedFromOrder
                  ? withoutOrderOnly(SUPPLIER_DELIVERY_TERMS_OPTIONS, supplierDeliveryTerms)
                  : SUPPLIER_DELIVERY_TERMS_OPTIONS}
                placeholder="Select terms..."
                searchPlaceholder="Search terms..."
              />
            </div>

            <div
              className="form-group po-field-terms"
              title={selfBilled
                ? 'Our delivery terms on this self-billed PO — how the goods reach the delivery / storage address. Any value.'
                : raisedFromOrder
                  ? 'Our delivery terms, on the order. Changing them here changes the order. Direct Delivery and Third-Party are set on the order.'
                  : stockTP
                    ? STOCK_OUR_TERMS_HINT
                    : 'Our delivery terms belong to a customer order — this PO has none (a self-billed PO keeps its own, and a stock PO collected by a third party is Third-Party Delivery).'}
            >
              <label>Our Delivery Terms{selfBilled && <> <span className='required-field'>*</span></>}</label>
              <SearchableDropdown
                value={(raisedFromOrder || selfBilled) ? ourDeliveryTerms : (stockTP ? stockOurTerms(supplierDeliveryTerms) : '')}
                onChange={(value) => {
                  const next = pairSelfBilled('ours', value,
                    pairOnChoose('ours', value, { supplier: supplierDeliveryTerms, ours: ourDeliveryTerms }));
                  setOurDeliveryTerms(next.ours);
                  if (next.supplier !== supplierDeliveryTerms) setSupplierDeliveryTerms(next.supplier);
                }}
                options={selfBilled ? SELF_BILLED_OUR_TERMS_OPTIONS : stockTP
                  ? [{ value: THIRD_PARTY_OUR_TERMS, label: THIRD_PARTY_OUR_TERMS }] : withoutOrderOnly(
                  ourDeliveryTerms === DIRECT_DELIVERY_TERMS
                    ? [{ value: DIRECT_DELIVERY_TERMS, label: DIRECT_DELIVERY_TERMS }, ...ourDeliveryTermsOptions(ourDeliveryTerms)]
                    : ourDeliveryTermsOptions(ourDeliveryTerms),
                  ourDeliveryTerms)}
                placeholder={(raisedFromOrder || selfBilled) ? 'Select terms...' : 'No order'}
                searchPlaceholder="Search terms..."
                disabled={!(raisedFromOrder || selfBilled)}
              />
            </div>

            <div className="form-group po-field-120" title={poAfterDelivery ? AFTER_DELIVERY_STATUS_HINT : undefined}>
              <label>Status</label>
              {poAfterDelivery ? (
                // Forward only (utils/poStatusMove): the steps behind it, Billed
                // and Archived are shown greyed with the reason.
                <Dropdown
                  value={status}
                  onChange={(value) => setStatus(value)}
                  options={statusOptionsAfterDelivery(loadedStatus || status)}
                />
              ) : (
                <SearchableDropdown
                  value={status}
                  onChange={(value) => setStatus(value)}
                  options={PO_STATUS_OPTIONS}
                  searchPlaceholder="Search status..."
                />
              )}
            </div>

            {/* SELF-BILLED: under Status, in its own grid row — us
                buying from a customer (utils/selfBilledPo). */}
            <div
              className={`form-group po-self-billed-field${selfBilled ? ' on' : ''}`}
              title={selfBilledBlock || SELF_BILLED_HINT}
            >
              <label htmlFor="po-self-billed">Self-billed</label>
              <div className="po-self-billed-switch">
                <div className="checkbox-container">
                  <div className="check light">
                    <input
                      id="po-self-billed"
                      type="checkbox"
                      checked={selfBilled}
                      disabled={!!selfBilledBlock}
                      onChange={(e) => handleSelfBilledChange(e.target.checked)}
                    />
                    <label htmlFor="po-self-billed"></label>
                  </div>
                </div>
                <span className="po-self-billed-state">{selfBilled ? 'On' : 'Off'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Line Details */}
        <div className="form-section">
          <h2>Line Details</h2>
          <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 'var(--spacing-md)', marginTop: '-8px' }}>
            <span className="material-symbols-rounded" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '4px' }}>info</span>
            {transportRuleNow.phase === TRANSPORT_PHASE_BOARD
              ? `${transportRuleNow.reason}.${selfBilled ? ' Self-billed: it never has to be planned.' : ''}`
              : transportRuleNow.phase === TRANSPORT_PHASE_CORRECTION
                ? (transportLockedReason
                  ? `${transportLockedReason}.`
                  : 'The delivery day has passed: a haulier, driver or vehicle changed here corrects the record, and the planning board\'s record of that day with it.')
                : transportRuleNow.phase === TRANSPORT_PHASE_FREE
                  ? 'This PO is not on a planning board — set its haulier, driver and vehicle here.'
                  : 'Customer Collection — the customer collects, so there is no haulier, driver or vehicle.'}
          </p>
          {poAfterDelivery && (
            <div className="po-after-delivery-note" role="note">
              <span className="material-symbols-rounded">local_shipping</span>
              <div className="po-after-delivery-body">
                <span>{poAfterDelivery.reason} Corrections here are for our records — nothing is sent to the supplier. {AFTER_DELIVERY_STATUS_HINT}.</span>
                <Input
                  type="text"
                  value={correctionReason}
                  onChange={(e) => setCorrectionReason(e.target.value)}
                  placeholder="Reason for the correction (optional — kept in its history)"
                  maxLength={500}
                  aria-label="Reason for the correction (optional)"
                />
              </div>
            </div>
          )}
          <OrderLinesGrid
            variant="purchase"
            closed={poClosedCells(poAfterDelivery)}
            transportLockedReason={transportLockedReason}
            transportFixedLabel={transportFixedLabel}
            lines={lineItems}
            onChange={setLineItems}
            inventoryItems={products}
            previousItems={previousItems}
            onRemovePrevious={removePrevious}
            suppliers={suppliers}
            supplierAccountId={selectedSupplierId ? parseInt(selectedSupplierId, 10) : null}
            hauliers={hauliers}
            drivers={drivers}
            vehicles={vehicles}
            onAddItem={setAddItemForRow}
            onItemPicked={fillSupplierFromItems}
            accounts={purchaseAccounts}
            requireAccounts={(line) => accountRefusals.some(r => r.id === line.id)}
            removeBlock={editMode ? (line => (line.orderLineId
              ? `Bought for ${linkedOrderNumber || 'a customer order'} — to change the product, choose the new item on this line; to drop it, remove it on the order`
              : null)) : null}
            
            footerRight={<LineTotalsPanel lines={lineItems} />}
          />

          {/* Under the lines, as on every create screen built on this grid:
              where it goes and what the driver needs to know on the left,
              reset and save on the right. */}
          <div className="doc-footer">
            <div className="doc-footer-main">
              <div className="po-delivery-row">
                <div className="po-deliver-col">
                  <div
                    className="form-group"
                    title={selfBilled
                      ? CUSTOMER_FIXED_REASON
                      : poAfterDelivery
                        ? `${linkedOrderNumber || 'Its order'} is delivered — to send it to another customer, use Divert`
                        : linkedOrderNumber
                          ? `Changing it moves ${linkedOrderNumber} to the new customer too, and its other purchase orders with it`
                          : undefined}
                  >
                    <label>Customer{linkedOrderNumber ? ` (for ${linkedOrderNumber})` : ''}</label>
                    <SearchableDropdown
                      value={selfBilled ? '' : selectedCustomerId}
                      disabled={!!poAfterDelivery || selfBilled}
                      onChange={handleCustomerChange}
                      options={[
                        { value: '', label: linkedOrderNumber ? 'Select customer...' : 'None — delivered to us' },
                        ...customers.map(c => ({
                          value: c.id.toString(),
                          badge: c.account_no || ' ',
                          label: c.company_name,
                          searchText: `${c.account_no || ''} ${c.company_name}`
                        }))
                      ]}
                      placeholder="Select customer..."
                      searchPlaceholder="Search by # or name..."
                    />
                  </div>
                  {selfBilled ? (
                    /* Self-billed: where the goods go is chosen here, apart
                       from the customer — STOCK (our own site) pinned first,
                       or any contact. Required. */
                    <div className="form-group po-self-billed-address">
                      <label>Delivery / storage address <span className='required-field'>*</span></label>
                      <SearchableDropdown
                        value={deliveryTarget}
                        onChange={(v) => setDeliveryTarget(v || '')}
                        options={deliveryTargetOptions(allContacts || [])}
                        placeholder={allContacts === null ? 'Loading contacts…' : ADDRESS_PLACEHOLDER}
                        placeholderClassName={deliveryTarget ? '' : 'sd-placeholder-error'}
                        searchPlaceholder="Search by # or name, or choose STOCK..."
                      />
                      <div className={`po-deliver-to${selfBilledAddressText ? '' : ' empty'}`}>
                        {selfBilledAddressText || 'Where are the goods going? Choose STOCK for our own site, or the contact they are stored with.'}
                      </div>
                      {haulageFields}
                    </div>
                  ) : (
                  <div className={`form-group${stockTP ? ' po-self-billed-address' : ''}`}>
                    <label>Deliver To</label>
                    {/* The address this PO will print. A new PO delivers to us; one
                        raised from a customer order keeps the delivery address it was
                        given, which is not ours to show as ours. */}
                    <div className="po-deliver-to">
                      {deliveryAddress || 'Excel Agri Ltd.\nThe Old Grain Drier\nDolton Beacon\nWinkleigh\nDevon\nEX19 8PS'}
                    </div>
                    {/* A stock PO collected by a third party: who brings it to
                        our site, and its PO-H (utils/stockHaulage). */}
                    {stockTP && haulageFields}
                    {!stockTP && stockHaulageReason && (
                      <span className="po-self-billed-haulage-po po-stock-haulage-reason">{stockHaulageReason}</span>
                    )}
                  </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Delivery Instructions</label>
                  <textarea
                    className="doc-textarea"
                    value={deliveryInstructions}
                    onChange={(e) => setDeliveryInstructions(e.target.value)}
                    placeholder={selfBilled
                      ? 'Anything else about the collection or storage — who collects, opening times, where on site...'
                      : 'Anything the supplier or driver needs to know...'}
                    rows={5}
                  />
                </div>
              </div>
            </div>
            <div className="doc-footer-actions">
              <button className="btn-slideout" onClick={requestReset} disabled={loading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">replay</span>
                </div>
                <span className="btn-label">{editMode ? 'Discard Changes' : 'Reset'}</span>
              </button>
              <button className="btn-slideout btn-slideout-primary" onClick={handleSavePO}
                disabled={loading || accountRefusals.length > 0} title={accountRefusalText || undefined}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">save</span>
                </div>
                <span className="btn-label">{editMode ? 'Update PO' : 'Save PO'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSupplierModal && selectedSupplierId && (
        <ContactModal
          contactId={selectedSupplierId}
          onClose={() => setShowSupplierModal(false)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        onCancel={confirmDialog.onCancel || (() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null }))}
        secondaryText={confirmDialog.secondaryText || null}
        onSecondary={confirmDialog.onSecondary || null}
        {...(confirmDialog.secondaryIcon ? { secondaryIcon: confirmDialog.secondaryIcon } : {})}
        {...(confirmDialog.confirmIcon ? { confirmIcon: confirmDialog.confirmIcon } : {})}
      />

      <ConfirmDialog
        isOpen={confirmReset}
        title={editMode ? 'Discard changes?' : 'Reset this purchase order?'}
        message={editMode
          ? 'Go back to this purchase order as it is saved? Anything changed since will be lost.'
          : 'Clear the supplier, references, delivery instructions and every line?'}
        confirmText={editMode ? 'Discard' : 'Reset'}
        onConfirm={handleReset}
        onCancel={() => setConfirmReset(false)}
      />

      {/* New supplier, created without leaving the PO. The list is reloaded on
          save and the new supplier chosen, so the next thing to do is the
          lines. The modal closes itself — after any follow-up it has to ask. */}
      {showNewSupplier && (
        <ContactEditModal
          contact={{ contact_type: 'SUPPLIER' }}
          onClose={() => setShowNewSupplier(false)}
          onSave={async (_createdInXero, created) => {
            // Chosen from the reloaded list, so its address, phone and email
            // arrive in the same shape as any other supplier's.
            const list = await loadSuppliers();
            if (created?.id) handleSupplierChange(created.id.toString(), list);
          }}
        />
      )}

      {/* Post-Save Dialog */}
      {postSaveDialog.isOpen && createPortal(
        <div className="modal-overlay">
          <div className="modal-content modal-sm">
            <div className="modal-header" style={{ border: "none", padding: "var(--spacing-xl) var(--spacing-xl) var(--spacing-lg)" }}>
              <h4>Purchase Order Created</h4>
            </div>
            <div className="modal-body" style={{ padding: "0 var(--spacing-xl) var(--spacing-md)" }}>
              <p>Purchase Order <strong>{postSaveDialog.poNumber}</strong> has been created successfully.</p>
              {postSaveDialog.haulage && (
                <p>
                  Haulage order <strong>{postSaveDialog.haulage.numbers}</strong> was raised to {postSaveDialog.haulage.haulier || 'the haulier'} to
                  collect it — {postSaveDialog.haulage.sent ? 'sent.' : 'not sent yet (send it from the PO window when ready).'}
                </p>
              )}
              {postSaveDialog.sent
                ? <p>It has been sent to {postSaveDialog.supplier || 'the supplier'}.</p>
                : <p>Send it to {postSaveDialog.supplier || 'the supplier'} now?</p>}
            </div>
            <div className="modal-footer" style={{ display: 'flex', flexDirection: 'row', gridTemplateRows: '1fr 2fr 3fr' }}>
              <button data-read-safe className="btn-round btn-danger" onClick={handleCancel}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Close</span>
              </button>
              <button
                className={`btn-slideout${postSaveDialog.sent ? '' : ' btn-slideout-primary'}`}
                onClick={handleSendToSupplier}
                disabled={postSaveDialog.sent}
                title={postSaveDialog.sent ? 'Already sent' : 'Email the PO to the supplier'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">send</span>
                </div>
                <span className="btn-label">{postSaveDialog.sent ? 'Sent' : 'Send to Supplier'}</span>
              </button>
              <button className="btn-slideout" onClick={handleViewPO}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">visibility</span>
                </div>
                <span className="btn-label">View PO</span>
              </button>
              <button className="btn-slideout" onClick={handleCreateAnother}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Create Another PO</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Sending the PO just created. Closing it — sent or not — brings the
          post-save dialog back, so View PO / Create Another are still there. */}
      {sendFlowPo && (
        <SendToSupplierFlow
          linkedPos={[sendFlowPo]}
          orderNumber={sendFlowPo.purchase_order_number}
          user={user}
          onSent={() => setPostSaveDialog(prev => ({ ...prev, sent: true }))}
          onOpenInPOSearch={() => { setSendFlowPo(null); handleViewPO(); }}
          onClose={() => {
            setSendFlowPo(null);
            setPostSaveDialog(prev => ({ ...prev, isOpen: true }));
          }}
        />
      )}

      {/* A customer change reached POs a supplier or haulier already holds. */}
      {revisedPrompt && !revisedFlow && (
        <ConfirmDialog
          isOpen
          type="info"
          title={revisedPrompt.pos.every(p => p.cancellation)
            ? (revisedPrompt.pos.length === 1 ? 'Send the cancellation?' : 'Send the cancellations?')
            : revisedPrompt.pos.length === 1 ? 'Send the revised purchase order?' : 'Send the revised purchase orders?'}
          message={revisedPrompt.message}
          confirmText="Send now"
          cancelText="Not now"
          onConfirm={() => setRevisedFlow(revisedPrompt)}
          onCancel={() => { const done = revisedPrompt; setRevisedPrompt(null); done.next(); }}
        />
      )}
      {revisedFlow && (
        <SendToSupplierFlow
          linkedPos={revisedFlow.pos}
          orderNumber={revisedFlow.pos[0]?.purchase_order_number}
          user={user}
          onClose={() => {
            const done = revisedFlow;
            setRevisedFlow(null);
            setRevisedPrompt(null);
            done.next();
          }}
        />
      )}

      {/* A PO-H this save newly raised for the haulier (2026-10-29). */}
      {haulagePrompt && !haulageFlow && (
        <ConfirmDialog
          isOpen
          type="info"
          title="Send the haulage order?"
          message={haulagePrompt.message}
          confirmText="Send"
          confirmIcon="forward_to_inbox"
          cancelText="Not now"
          onConfirm={() => setHaulageFlow(haulagePrompt)}
          onCancel={() => { const done = haulagePrompt; setHaulagePrompt(null); done.next(false); }}
        />
      )}
      {haulageFlow && (
        <SendToSupplierFlow
          linkedPos={haulageFlow.pos}
          orderNumber={haulageFlow.pos[0]?.purchase_order_number}
          user={user}
          onSent={() => { haulageSentRef.current = true; }}
          onClose={() => {
            const done = haulageFlow;
            setHaulageFlow(null);
            setHaulagePrompt(null);
            done.next(haulageSentRef.current);
          }}
        />
      )}

      {/* Create an inventory item without leaving a half-built PO. The new item
          lands on the row that asked for it, at its own cost. */}
      <InventoryItemFormModal
        isOpen={addItemForRow !== null}
        item={null}
        onClose={() => setAddItemForRow(null)}
        onSaved={async (saved) => {
          const rowIndex = addItemForRow;
          setAddItemForRow(null);
          if (!saved) return;
          const items = await loadProducts();
          const fresh = items.find(i => i.id === saved.id) || saved;
          const resolved = resolveItemForVariant('purchase', fresh);
          setLineItems(prev => {
            if (rowIndex === null || !prev[rowIndex]) return prev;
            const next = [...prev];
            // The new item's purchase words (utils/pickedItemWords, 2026-10-28).
            const { product_name: _n, ...rest } = resolved;
            next[rowIndex] = { ...next[rowIndex], ...rest, ...wordsOnItemPick('purchase', next[rowIndex], fresh) };
            return next;
          });
        }}
      />

      {invoiceOffer && (
        <InvoiceEditOfferDialog
          offer={invoiceOffer.offer}
          orderId={invoiceOffer.orderId}
          reason={invoiceOffer.reason}
          onDone={(text, type) => {
            const next = invoiceOffer.next;
            setInvoiceOffer(null);
            if (text) {
              setAlertDialog({ isOpen: true, message: text, type: type || 'info', onCloseCallback: next });
            } else {
              next();
            }
          }}
        />
      )}

      <PostedEditDialog
        isOpen={!!reasonPrompt}
        title={`Change ${poNumber || 'this purchase order'}?`}
        intro={reasonPrompt?.message || 'This purchase order has been billed. Say why it is being changed — the reason is kept in its history.'}
        saving={loading}
        confirmLabel="Update PO"
        onConfirm={(reason) => handleSavePO({ ...(reasonPrompt?.opts || {}), statusChecked: true, editReason: reason })}
        onCancel={() => setReasonPrompt(null)}
      />

      {previousItemToast}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => {
          const callback = alertDialog.onCloseCallback;
          setAlertDialog({ isOpen: false, message: '', type: 'info', onCloseCallback: null });
          if (callback) callback();
        }}
      />
      </div>
    </div>
  );
}

export default CreatePurchaseOrder;
